const API_BASE = '/api';

interface RequestOptions {
  method?: string;
  body?: unknown;
  params?: Record<string, string | number | undefined>;
  signal?: AbortSignal;
  skipAuth?: boolean;
  _retried?: boolean;
}

class ApiError extends Error {
  status: number;
  detail: string;

  constructor(status: number, message: string, detail = '') {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }
}

function getToken(): string | null {
  try {
    return localStorage.getItem('auth_token');
  } catch {
    return null;
  }
}

async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, params, signal, skipAuth = false, _retried = false } = options;

  let url = `${API_BASE}${endpoint}`;

  if (params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        searchParams.set(key, String(value));
      }
    });
    const qs = searchParams.toString();
    if (qs) url += `?${qs}`;
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (!skipAuth) {
    const token = getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  const fetchOptions: RequestInit = {
    method,
    headers,
    signal,
  };

  if (body && method !== 'GET') {
    fetchOptions.body = JSON.stringify(body);
  }

  const response = await fetch(url, fetchOptions);

  if (!response.ok) {
    if (response.status === 401 && !skipAuth && !_retried) {
      const rt = localStorage.getItem('refresh_token');
      if (rt) {
        try {
          const r = await fetch(`${API_BASE}/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken: rt }),
          });
          if (r.ok) {
            const data = await r.json();
            localStorage.setItem('auth_token', data.token);
            if (data.refreshToken) localStorage.setItem('refresh_token', data.refreshToken);
            return request<T>(endpoint, { ...options, _retried: true });
          }
        } catch {
          // fallthrough to error
        }
      }
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
    }

    let detail = '';
    try {
      const errorBody = await response.json();
      detail = errorBody?.message || errorBody?.detail || '';
    } catch {
      // ignore parse errors
    }
    throw new ApiError(response.status, `请求失败 (${response.status})`, detail);
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

export { request, ApiError };
export type { RequestOptions };